import { PolicyAddDependantModalComponent } from './../components/policy-add-dependant-modal/policy-add-dependant-modal.component';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { forkJoin, Subscription } from 'rxjs';
import { PolicyDto, ProductDto, ProductOfferDto, ProductRuleDto, ProductExtendedDto, PolicyDependantDto, ProductOfferLineDto, PolicyStatusEnum, PolicyBeneficiaryDto, InsurancePremiumRecipientDto } from '../../../../service/dto-insurance';
import { AgentDto, DependantDto, EmployerDto, MemberDto } from '../../../../service/dto';
import { HttpInsurancePolicyService } from '../../../../service/http-insurance-policy.service';
import { PolicyExtendedCoverModalComponent } from '../components/policy-extended-cover-modal/policy-extended-cover-modal.component';
import { DependantWrapper, RuleResult } from '../policy-new/policy-new.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
import { PolicyBeneficiaryModalComponent } from '../components/policy-beneficiary-modal/policy-beneficiary-modal.component';
import { HttpEmployerService } from '../../../../service/http-employer.service';
import { HttpInsuranceProductService } from '../../../../service/http-insurance-product.service';

@Component({
	selector: 'app-policy-edit',
	templateUrl: './policy-edit.component.html',
	styleUrls: ['./policy-edit.component.scss'],
	standalone: false
})
export class PolicyEditComponent implements OnInit, OnDestroy {
	policy!: PolicyDto;
	subscription = new Subscription();

	loading = false;
	dependants: DependantDto[] = [];
	policyForm?: FormGroup;
	dependantWrappers: DependantWrapper[] = [];


	currentPremiumCompulsory: string = '';
	currentPremiumAddOn: string = '';
	currentPremiumExtended: string = '';
	currentPremiumTotal: string = '';

	availablePolicyGroups: InsurancePremiumRecipientDto[] = [];
	availableEmployers: EmployerDto[] = [];

	currencyFormatter = (value: number) => `R ${value}`;
	currencyParser = (value: string) => value.replace(/R\s?/, '') as any;
	compareById = (o1: any, o2: any): boolean => o1?.id === o2?.id;

	constructor(
		private route: ActivatedRoute,
		private policyService: HttpInsurancePolicyService,
		private modalService: NzModalService,
		private httpEmployerService: HttpEmployerService,
		private httpServiceProductService: HttpInsuranceProductService,
		private fb: FormBuilder,
		private currencyPipe: CurrencyPipe) { }

	ngOnInit(): void {

		forkJoin({
			premiumRecipients: this.httpServiceProductService.getPremiumRecipients(),
			employers: this.httpEmployerService.getAll()
		}).subscribe(({ premiumRecipients, employers }) => {
			this.availablePolicyGroups = premiumRecipients.filter(item => item.type === 'InsurancePolicyGroup');
			this.availableEmployers = employers;
		});

		this.subscription.add(this.route.paramMap.subscribe(paramMap => {
			this.loading = false;

			this.policyService.get(+paramMap.get('policyId')!).subscribe({
				next: (policy) => {
					this.setup(policy);
					this.loading = false;
				},
				error: () => {
					this.loading = false;
				}
			})
		}));
	}
	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
	setup(policy: PolicyDto) {
		this.policy = policy;
		this.dependants = [...this.policy.dependants.map(i => i.dependant)];
		for (let dependant of this.policy.member!.dependants) {
			if (this.dependants.findIndex(i => i.id == dependant.id) == -1) {
				this.dependants.push(dependant);
			}
		}
		this.wrapDependants();
		for (let dependantWrapper of this.dependantWrappers) {
			const policyDependant = this.policy.dependants.filter(item => item.dependant.id == dependantWrapper.dependant.id)[0];
			if (!policyDependant) {
				dependantWrapper.selected = false;
			} else {
				dependantWrapper.selected = true;
				dependantWrapper.extended = policyDependant.productExtended;
				dependantWrapper.fromDate = policyDependant.fromDate;
			}
		}




		this.policyForm = this.fb.group({
			policyNumber: [policy.policyNumber, Validators.required],
			policyNumberAlt: [policy.policyNumberAlt],
			currentSalary: [policy.currentSalary],
			policyNumberOwls: [policy.policyNumberOwls],
			policyNumberIndustry: [policy.policyNumberIndustry],
			policyNumberMembership: [policy.policyNumberMembership],
			currentLoanAmount: [policy.currentLoanAmount],

			jobTitle: [policy.jobTitle],
			occupation: [policy.occupation],
			occupationDate: [policy.occupationDate],

			employer: [policy.employer],
			policyGroup: [policy.policyGroup],

			fromDate: [policy.fromDate, Validators.required],
			toDate: [policy.toDate],
		});
		this.policy.productOffer?.lines.forEach(line => {
			if (!line.compulsory) {
				line.enabled = this.policy.productOfferLines.filter(item => item.id == line.id)[0] != undefined;
			}
		});

		this.currentPremiumCompulsory = this.getCompulsoryPremiums();
		this.currentPremiumAddOn = this.getAddOnPremiums();
		this.currentPremiumExtended = this.getExtendedPremiums();
		this.currentPremiumTotal = this.getTotalPremiums();
	}

	get isCaptureSalary(): boolean {
		if (!this.policy.product) {
			return false;
		}
		return this.policy.product.productType == 'Funeral' ? false : true;
	}
	get isDependantEnabled(): boolean {
		if (!this.policy.product) {
			return false;
		}
		return this.policy.product.productType == 'Funeral';
	}

	wrapDependants() {
		this.dependantWrappers = this.dependants.map(d => this.wrapDependant(d))
			.sort((i1, i2) => {
				if (i1.eligible != i2.eligible) {
					return i1.eligible ? -1 : 1;
				}
				let out = i1.dependant.surname.localeCompare(i2.dependant.surname);
				if (out != 0) {
					return out;
				}
				return i1.dependant.name.localeCompare(i2.dependant.name);
			});
	}
	wrapDependant(dependant: DependantDto): DependantWrapper {
		const applicableRules = [...this.policy.product!.rules, ...this.policy.productOffer!.rules]
			.filter(item => item.appliedTo === dependant.relationship || item.appliedTo === 'All');
		const failedRules: RuleResult[] = [];
		if (!dependant.relationshipIsClose) {
			failedRules.push({
				ruleType: 'Relationship',
				passed: false,
				reason: `${dependant.relationship} dependants are only allowed extended cover.`
			});
		}

		for (const rule of applicableRules) {
			const passed = this.evaluateRule(rule, dependant);

			if (!passed) {
				failedRules.push({
					ruleType: rule.ruleType,
					passed: false,
					reason: this.getFailureReason(rule, dependant)
				});
			}
		}
		console.log("Validate Dependant: " + dependant, failedRules);
		return {
			dependant: dependant,
			eligible: dependant.relationshipIsClose && failedRules.length === 0,
			failedRules: failedRules,
			selected: failedRules.length === 0,
			extended: undefined,
			fromDate: undefined,
			age: this.calculateAge(dependant.dateOfBirth)
		};
	}
	evaluateRule(rule: ProductRuleDto, dependant: DependantDto): boolean {
		const age = this.calculateAge(dependant.dateOfBirth);

		switch (rule.ruleType) {

			case 'EntryAgeFrom':
				return age >= rule.threshold;

			case 'EntryAgeTo':
				return age <= rule.threshold;

			case 'MinAge':
				return age >= rule.threshold;

			case 'MaxAge':
				return age <= rule.threshold;

			case 'InstanceCount':
			case 'WaitingPeriodSuicide':
			case 'WaitingPeriodAccidental':
			case 'WaitingPeriodNatural':
				return true;
			default:
				return true;
		}
	}



	calculateAge(dob: Date): number {
		const today = new Date();
		const birth = new Date(dob);

		let age = today.getFullYear() - birth.getFullYear();
		const m = today.getMonth() - birth.getMonth();

		if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
			age--;
		}

		return age;
	}
	getFailureReason(rule: ProductRuleDto, dependant: DependantDto): string {
		const age = this.calculateAge(dependant.dateOfBirth);
		switch (rule.ruleType) {
			case 'MinAge':
				return `Dependants age of${age} is below the minimum allowed age of ${rule.threshold}.`;
			case 'MaxAge':
				return `Dependants age of${age} is above the maximum allowed age of ${rule.threshold}.`;
			case 'EntryAgeFrom':
				return `Dependants age of${age} is below the minimum allowed entry age of ${rule.threshold}.`;
			case 'EntryAgeTo':
				return `Dependants age of${age} is above the maximum allowed entry age of ${rule.threshold}.`;
			default:
				return `Failed ${rule.ruleType}.  Age:[${age}]`;
		}
	}

	getOfferLinePrice(input: ProductOfferLineDto): string {
		const detail = input.details[0];
		if (detail.premiumType == 'Fixed') {
			return this.currencyPipe.transform(detail.premiumAmount, 'ZAR', 'symbol-narrow')!;;
		}
		const monthlySalary = this.policyForm!.get('currentSalary')?.value;
		if (detail.premiumPercentageProperty == 'client.monthlySalary') {
			if (!monthlySalary) {
				return `${detail.premiumAmount} % of Current Salary`
			}
			const out = (+monthlySalary) * (detail.premiumAmount / 100);
			return this.currencyPipe.transform(out, 'ZAR', 'symbol-narrow')!;

		}
		if (detail.premiumPercentageProperty == 'client.annualSalary') {
			if (!monthlySalary) {
				return `${detail.premiumAmount} % of Annual Salary`
			}
			const out = (+monthlySalary + 12) * (detail.premiumAmount / 100);
			return this.currencyPipe.transform(out, 'ZAR', 'symbol-narrow')!;
		}
		return 'Invalid Property(' + detail.premiumPercentageProperty + ')';
	}

	getCompulsoryPremiums(): string {
		let out = 0;
		for (const line of this.policy.productOffer!.lines) {
			if (line.compulsory) {
				if (line.details[0].premiumType == 'Fixed') {
					out += line.details[0].premiumAmount;
				} else {
					const monthlySalary = this.policyForm!.get('currentSalary')?.value;
					if (line.details[0].premiumPercentageProperty == 'client.monthlySalary') {
						if (!monthlySalary) {
							return "CurrentSalary is required";
						}
						out += (+monthlySalary) * (line.details[0].premiumAmount / 100);
					} else if (line.details[0].premiumPercentageProperty == 'client.annualSalary') {
						if (!monthlySalary) {
							return "CurrentSalary is required";
						}
						out += (+monthlySalary + 12) * (line.details[0].premiumAmount / 100);
					} else {
						return 'Invalid Property(' + line.details[0].premiumPercentageProperty + ')';
					}
				}
			}
		}
		return this.currencyPipe.transform(out, 'ZAR', 'symbol-narrow')!;
	}
	getAddOnPremiums(): string {
		let out = 0;
		for (const line of this.policy.productOffer!.lines) {
			if (line.compulsory == false && line.enabled) {
				if (line.details[0].premiumType == 'Fixed') {
					out += line.details[0].premiumAmount;
				} else {
					const monthlySalary = this.policyForm!.get('currentSalary')?.value;
					if (line.details[0].premiumPercentageProperty == 'client.monthlySalary') {
						if (!monthlySalary) {
							return "CurrentSalary is required";
						}
						out += (+monthlySalary) * (line.details[0].premiumAmount / 100);
					} else if (line.details[0].premiumPercentageProperty == 'client.annualSalary') {
						if (!monthlySalary) {
							return "CurrentSalary is required";
						}
						out += (+monthlySalary + 12) * (line.details[0].premiumAmount / 100);
					} else {
						return 'Invalid Property(' + line.details[0].premiumPercentageProperty + ')';
					}
				}
			}
		}
		return this.currencyPipe.transform(out, 'ZAR', 'symbol-narrow')!;

	}
	getExtendedPremiums(): string {
		let out = 0;
		for (const dependant of this.dependantWrappers) {
			if (dependant.extended) {
				const detail = dependant.extended.details.filter(item => item.fromAge <= dependant.age && (!item.toAge || item.toAge >= dependant.age))[0];
				if (detail.premiumType == 'Fixed') {
					out += detail.premiumAmount;
				} else {
					const monthlySalary = this.policyForm!.get('currentSalary')?.value;
					if (detail.premiumPercentagePropety == 'client.monthlySalary') {
						if (!monthlySalary) {
							return "CurrentSalary is required";
						}
						out += (+monthlySalary) * (detail.premiumAmount / 100);
					} else if (detail.premiumPercentagePropety == 'client.annualSalary') {
						if (!monthlySalary) {
							return "CurrentSalary is required";
						}
						out += (+monthlySalary + 12) * (detail.premiumAmount / 100);
					} else {
						return 'Invalid Property(' + detail.premiumPercentagePropety + ')';
					}
				}
			}
		}
		return this.currencyPipe.transform(out, 'ZAR', 'symbol-narrow')!;
	}
	getTotalPremiums(): string {
		let out = 0;
		for (const line of this.policy.productOffer!.lines) {
			if (line.compulsory || line.enabled) {
				if (line.details[0].premiumType == 'Fixed') {
					out += line.details[0].premiumAmount;
				} else {
					const monthlySalary = this.policyForm!.get('currentSalary')?.value;
					if (line.details[0].premiumPercentageProperty == 'client.monthlySalary') {
						if (!monthlySalary) {
							return "CurrentSalary is required";
						}
						out += (+monthlySalary) * (line.details[0].premiumAmount / 100);
					} else if (line.details[0].premiumPercentageProperty == 'client.annualSalary') {
						if (!monthlySalary) {
							return "CurrentSalary is required";
						}
						out += (+monthlySalary + 12) * (line.details[0].premiumAmount / 100);
					} else {
						return 'Invalid Property(' + line.details[0].premiumPercentageProperty + ')';
					}
				}
			}
		}
		for (const dependant of this.dependantWrappers) {
			if (dependant.extended) {
				const detail = dependant.extended.details.filter(item => item.fromAge <= dependant.age && (!item.toAge || item.toAge >= dependant.age))[0];
				if (detail.premiumType == 'Fixed') {
					out += detail.premiumAmount;
				} else {
					const monthlySalary = this.policyForm!.get('currentSalary')?.value;
					if (detail.premiumPercentagePropety == 'client.monthlySalary') {
						if (!monthlySalary) {
							return "CurrentSalary is required";
						}
						out += (+monthlySalary) * (detail.premiumAmount / 100);
					} else if (detail.premiumPercentagePropety == 'client.annualSalary') {
						if (!monthlySalary) {
							return "CurrentSalary is required";
						}
						out += (+monthlySalary + 12) * (detail.premiumAmount / 100);
					} else {
						return 'Invalid Property(' + detail.premiumPercentagePropety + ')';
					}
				}
			}
		}
		return this.currencyPipe.transform(out, 'ZAR', 'symbol-narrow')!;
	}

	isLineSelected(line: ProductOfferLineDto): boolean {
		return line.compulsory! || line.enabled!;
	}

	toggleLine(line: ProductOfferLineDto, checked: boolean): void {
		if (line.compulsory) {
			line.enabled = true;
		} else {
			line.enabled = checked;
		}
	}

	toggleDependant(input: DependantWrapper, checked: boolean): void {
		input.selected = checked;
	}
	addDependant() {
		const modalRef = this.modalService.create({
			nzTitle: 'Add Dependant',
			nzContent: PolicyAddDependantModalComponent,
			nzFooter: null,
			nzWidth: 1024,
			nzData: {
				policy: this.policy!
			}
		});

		modalRef.componentInstance!.policy = this.policy!;

		modalRef.afterClose.subscribe(result => {
			if (result) {
				this.dependantWrappers.push(this.wrapDependant(result));
			}
		});
	}
	editDependant(input: DependantWrapper) {
		const modalRef = this.modalService.create({
			nzTitle: 'Edit Dependant',
			nzContent: PolicyAddDependantModalComponent,
			nzFooter: null,
			nzWidth: 1024,
			nzData: {
				dependant: input.dependant,
				policy: this.policy!
			}
		});

		modalRef.componentInstance!.dependant = input.dependant;
		modalRef.componentInstance!.policy = this.policy!;

		modalRef.afterClose.subscribe(result => {
			if (result) {
				const tmp = this.wrapDependant(result);

				input.dependant = tmp.dependant;
				input.eligible = tmp.eligible;
				input.age = tmp.age;
				input.failedRules = tmp.failedRules;
			}
		});
	}
	addBeneficiary() {
		const modalRef = this.modalService.create({
			nzTitle: 'Add Beneficiary',
			nzContent: PolicyBeneficiaryModalComponent,
			nzFooter: null,
			nzWidth: 1024,
			nzData: {
				policy: this.policy!
			}
		});

		modalRef.componentInstance!.policy = this.policy!;

		modalRef.afterClose.subscribe(result => {
			if (result) {
				this.policy.beneficiaries.push(result);
			}
		});
	}
	editBeneficiary(input: PolicyBeneficiaryDto) {
		const modalRef = this.modalService.create({
			nzTitle: 'Edit Beneficiary',
			nzContent: PolicyBeneficiaryModalComponent,
			nzFooter: null,
			nzWidth: 1024,
			nzData: {
				beneficiary: input,
				policy: this.policy!
			}
		});

		modalRef.componentInstance!.beneficiary = input;
		modalRef.componentInstance!.policy = this.policy!;

		modalRef.afterClose.subscribe(result => {
			if (result) {
				input.fromDate = result.fromDate;
				input.toDate = result.toDate;
				input.relationship = result.relationship;
				input.relationshipIsClose = result.relationshipIsClose;
				input.idNumber = result.idNumber;
				input.name = result.name;
				input.surname = result.surname;
				input.bankAccount = result.bankAccount;
				input.msisdn = result.msisdn;
				input.emailAddress = result.emailAddress;
				input.properties = result.properties;
			}
		});
	}
	openExtendedCoverModal(input: DependantWrapper) {
		const modalRef = this.modalService.create({
			nzTitle: 'Add Extended Cover',
			nzContent: PolicyExtendedCoverModalComponent,
			nzData: {
				input,
				product: this.policy.product
			},
			nzFooter: null,
			nzWidth: 600
		});
		modalRef.componentInstance!.dependant = input.dependant;
		modalRef.componentInstance!.current = input.extended;
		modalRef.componentInstance!.fromDate = input.fromDate;
		modalRef.componentInstance!.product = this.policy.product!;

		modalRef.afterClose.subscribe(result => {
			input.extended = result.extended;
			input.fromDate = result.fromDate;
		});
	}
	submit() {
		this.policy.productOfferLines = this.policy.productOffer!.lines.filter(item => item.enabled);
		this.policy.policyNumber = this.policyForm!.get('policyNumber')!.value;
		this.policy.policyNumberAlt = this.policyForm!.get('policyNumberAlt')!.value;
		this.policy.currentSalary = this.policyForm?.get('currentSalary')?.value;
		this.policy.fromDate = this.policyForm!.get('fromDate')!.value;
		this.policy.toDate = this.policyForm!.get('toDate')!.value;

		this.policy.policyNumberOwls = this.policyForm?.get('policyNumberOwls')!.value;
		this.policy.policyNumberIndustry = this.policyForm?.get('policyNumberIndustry')!.value;
		this.policy.policyNumberMembership = this.policyForm?.get('policyNumberMembership')!.value;
		this.policy.currentLoanAmount = this.policyForm?.get('currentLoanAmount')!.value;

		this.policy.jobTitle = this.policyForm?.get('jobTitle')!.value;
		this.policy.occupation = this.policyForm?.get('occupation')!.value;
		this.policy.occupationDate = this.policyForm?.get('occupationDate')!.value;


		this.policy.employer = this.policyForm?.get('employer')!.value;
		this.policy.policyGroup = this.policyForm?.get('policyGroup')!.value;

		this.policy.dependants = this.dependantWrappers
			.filter(item => {
				if (item.eligible) {
					return true;//item.selected;
				}
				return item.extended != undefined;
			})
			.map(item => ({
				id: 0,
				createdAt: new Date(),
				createdByAgent: undefined as any as AgentDto,
				dependant: item.dependant,
				productExtended: item.extended,
				fromDate: item.fromDate,
				covered: true,
				hashCode: 0
			}));
		this.policyService.update(this.policy.id!, this.policy).subscribe(result => this.setup(result));
	}
}
